import { Link, NavLink, useParams } from "react-router";
import { LEGAL_DOCS, type Block, type LegalDocKey } from "../legal/documents";
import { LEGAL_EFFECTIVE, OPERATOR_COMPLETE } from "../legal/operator";
import { LEGAL_VERSION, PRODUCT_NAME } from "../lib/config";
import { useTitle } from "../lib/title";

const TABS: [LegalDocKey, string][] = [
  ["terms", "Terms of service"],
  ["privacy", "Privacy policy"],
  ["dpa", "Data processing agreement"],
];

/** The terms of service, privacy policy and data processing agreement (content in src/legal). */
export function Legal() {
  const { doc = "terms" } = useParams();
  const key: LegalDocKey = doc in LEGAL_DOCS ? (doc as LegalDocKey) : "terms";
  const d = LEGAL_DOCS[key];
  useTitle(d.title);
  return (
    <main className="page legal">
      <p className="label">
        <Link to="/">← {PRODUCT_NAME}</Link> · Version <span className="figures">{LEGAL_VERSION}</span> · in effect from {LEGAL_EFFECTIVE}
      </p>
      <nav className="tabs legal-tabs" aria-label="Legal documents">
        {TABS.map(([k, label]) => (
          <NavLink key={k} to={`/legal/${k}`} className="tab">
            {label}
          </NavLink>
        ))}
      </nav>
      <h1 className="display">{d.title}</h1>
      <p className="lede">{d.summary}</p>
      {!OPERATOR_COMPLETE && (
        <p className="notice" role="note">
          Some details about who provides the service (shown in [brackets]) are still to be added.
        </p>
      )}
      <ol className="legal-sections">
        {d.sections.map((s, i) => (
          <li key={s.heading} id={slug(s.heading)}>
            <h2>
              <span className="figures">{String(i + 1).padStart(2, "0")}</span> {s.heading}
            </h2>
            {s.blocks.map((b, j) => (
              <BlockView key={j} block={b} />
            ))}
          </li>
        ))}
      </ol>
    </main>
  );
}

function BlockView({ block }: { block: Block }) {
  if (typeof block === "string") return <p>{block}</p>;
  if ("list" in block) {
    return (
      <ul>
        {block.list.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    );
  }
  const [head, ...rows] = block.table;
  return (
    <div className="legal-table">
      <table>
        <thead>
          <tr>
            {head!.map((h, i) => (
              <th key={i} scope="col">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {r.map((c, j) => (j === 0 ? <th key={j} scope="row">{c}</th> : <td key={j}>{c}</td>))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
